import React, { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { uniqBy } from 'lodash';
import { Grid } from '@mui/material';
import { Button, CheckboxField, SubHeader, Table } from 'vetrf-ui';
import { eaeuApi } from 'api/eaeu';
import { ObjectTypeId, TransactionStatusId } from 'api/dict/params';
import { EaeuInfo } from 'api/eaeu/types';
import {
  FunctionPermissions,
  GmpDto,
  GmpEaeuTransactionListItem,
} from 'modules/gmp/gmpRegistry/api/params';
import { AvailableTransactions } from 'components/EaeuTransactions/components/AvailableTransactions/AvailableTransactions';
import { ResultTransactionsModal } from 'components/EaeuTransactions/components/ResultTransactionsModal/ResultTransactionsModal';
import { SelectTransactionsModal } from 'components/EaeuTransactions/components/SelectTransactionsModal/SelectTransactionsModal';
import { SubmitTransactionsModal } from 'components/EaeuTransactions/components/SubmitTransactionsModal/SubmitTransactionsModal';
import { columns } from './columns';
import styles from './Transactions.module.scss';

type TransactionProps = {
  data: GmpDto;
  updateData: () => void;
  permissions: FunctionPermissions;
};

export const Transactions = ({ data, updateData, permissions }: TransactionProps) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [sending, setSending] = useState<boolean>(false);
  const [eaeuInfo, setEaeuInfo] = useState<EaeuInfo[]>([]);
  const [transactions, setTransactions] = useState<GmpEaeuTransactionListItem[]>([]);
  const [transactionsModalOpen, setTransactionsModalOpen] = useState(false);
  const [confirmTransactionsModalOpen, setConfirmTransactionsModalOpen] = useState(false);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [resultTransactionsInfo, setResultTransactionsInfo] = useState<EaeuInfo[] | null>(null);
  const [selectedTransactions, setSelectedTransacrtions] = useState<EaeuInfo[] | null>(null);

  const sendToEaeu = async (transactionIds: string[]) => {
    if (data.id) {
      setSending(true);
      await eaeuApi
        .sendEaeuTransaction(data.id, ObjectTypeId.GMP_COMPLIANCE_CONCLUSION, transactionIds)
        .then((res) => setResultTransactionsInfo(res))
        .catch((e) => setResultTransactionsInfo(e.response.data))
        .finally(() => {
          setConfirmTransactionsModalOpen(false);
          setSending(false);
          setResultModalOpen(true);
        });
    }
  };

  const openConfirmingModalIfIsOneTransaction = () => {
    setSelectedTransacrtions([eaeuInfo[0]]);
    setConfirmTransactionsModalOpen(true);
  };

  const onTransactionsModalClose = () => {
    setTransactionsModalOpen(false);
  };

  const onConfirmModalClose = () => {
    setConfirmTransactionsModalOpen(false);
    setSelectedTransacrtions(null);
  };

  const onResultModalClose = () => {
    setResultModalOpen(false);
    updateData();
  };

  const onSubmitSelectModal = (strings: string[]) => {
    if (strings.length) {
      const selectedTransactionsList = eaeuInfo.filter((el) =>
        strings.includes(el.transactionType.id)
      );
      setSelectedTransacrtions(selectedTransactionsList);
      setTransactionsModalOpen(false);
      setConfirmTransactionsModalOpen(true);
    }
  };

  const onSubmitConfirmModal = () => {
    const idList = selectedTransactions?.map((el) => el.transactionType.id);
    sendToEaeu(idList!);
  };

  const shouldShowHidden = useWatch({ name: 'showHidden' });

  useEffect(() => {
    eaeuApi
      .getEaeuTransactionInformation(data.id, ObjectTypeId.GMP_COMPLIANCE_CONCLUSION)
      .then((res) => setEaeuInfo(res))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (data.gmpEaeuTransactionList) {
      setTransactions(data.gmpEaeuTransactionList);
    }
  }, [data.gmpEaeuTransactionList]);

  useEffect(() => {
    if (shouldShowHidden) {
      setTransactions(data.gmpEaeuTransactionList);
    } else {
      setTransactions(
        data.gmpEaeuTransactionList.filter(
          (el) =>
            ![TransactionStatusId.ERROR, TransactionStatusId.NOT_COMPLETED].includes(
              el?.eaeuTransaction?.status?.id as TransactionStatusId
            )
        )
      );
    }
  }, [shouldShowHidden]);

  const availableTransactions = useMemo(
    () =>
      uniqBy(
        eaeuInfo.map((el) => el.transactionType),
        'code'
      ),
    [eaeuInfo]
  );

  return (
    <>
      <SubHeader
        header="Транзакции ЕАЭС – уведомления о выдаче (изменении статуса) сертификата"
        icon=" eaes"
        iconSize={100}
        level={4}
        toolbar={
          <>
            {permissions.eaes && (
              <Button
                className="m-right"
                icon="arrow-up"
                color="info"
                size="minier"
                text="Отправить"
                tooltip="Передача транзакции P.SS.15.TRN.006 в ИИС ЕАЭС"
                onClick={() =>
                  eaeuInfo?.length > 1
                    ? setTransactionsModalOpen(true)
                    : openConfirmingModalIfIsOneTransaction()
                }
                disabled={!eaeuInfo?.length}
              />
            )}
            <Link
              to={`/registry/gmp/registry/${data?.producer.id}/producerTransactions`}
              target="_blank"
              rel="noreferrer"
            >
              <Button
                suffixIcon="external-link"
                iconColor="white"
                color="purple"
                size="minier"
                text="Транзакции ЕАЭС по производителю"
              />
            </Link>
          </>
        }
      />

      <Grid container spacing={2}>
        <Grid item xs={12} className={styles.infoRowWrapper}>
          <AvailableTransactions transactions={availableTransactions} loading={loading} />
          <CheckboxField
            fieldClassName="col-sm-2"
            labelClassName="col-sm-10 control-label no-padding-left no-padding-right"
            labelText="Показать ошибочные"
            name="showHidden"
          />
        </Grid>
      </Grid>

      <div className="table-container">
        <Table columns={columns} keyFunction={(item) => item.id} dataList={transactions} />
      </div>

      {transactionsModalOpen && (
        <SelectTransactionsModal
          onClose={onTransactionsModalClose}
          onSubmit={onSubmitSelectModal}
          transactions={eaeuInfo}
        />
      )}

      {confirmTransactionsModalOpen && (
        <SubmitTransactionsModal
          onClose={onConfirmModalClose}
          onSubmit={onSubmitConfirmModal}
          transactions={selectedTransactions}
          sending={sending}
          gmpData={data}
        />
      )}

      {resultModalOpen && (
        <ResultTransactionsModal
          responseInfo={resultTransactionsInfo}
          onClose={onResultModalClose}
        />
      )}
    </>
  );
};
